import React, { createContext, useState, useCallback, useContext } from 'react';

// Create the wallet context
export const WalletContext = createContext();

/**
 * Wallet Provider Component
 * Manages wallet balance state across the application
 */
export const WalletProvider = ({ children }) => {
  const [walletBalance, setWalletBalance] = useState('0');
  const [userId, setUserId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Initialize userId from localStorage on mount
  React.useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user?.id) {
        setUserId(user.id);
      }
    } catch (err) {
      console.error('Error reading user from localStorage:', err);
    }
  }, []);

  // Function to fetch and update wallet balance
  const updateWalletBalance = useCallback(async (id) => {
    const targetUserId = id || userId;
    if (!targetUserId) return null;

    try {
      const response = await fetch(
        `https://backend.mainbazar777.site/api/fetch-user-balance/${targetUserId}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch wallet balance');
      }

      const data = await response.json();
      const balance = data?.amount || '0';
      setWalletBalance(balance);
      setLastUpdated(new Date());
      return balance;
    } catch (err) {
      console.error('Error fetching wallet balance:', err);
      return null;
    }
  }, [userId]);

  // Function to set balance directly (for immediate updates)
  const setBalance = useCallback((balance) => {
    setWalletBalance(balance);
    setLastUpdated(new Date());
  }, []);

  const value = {
    walletBalance,
    userId,
    setUserId,
    setBalance,
    updateWalletBalance,
    lastUpdated
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

/**
 * Custom hook to use wallet context
 * @returns {Object} Wallet context value
 */
export const useWallet = () => {
  const context = useContext(WalletContext);
  
  if (!context) {
    throw new Error('useWallet must be used within WalletProvider');
  }

  return context;
};
